/* Phase 248 — Shared "My Project Detail" page used by BOTH the in-house team
 * portal and the supplier portal. Mode is determined by the URL prefix
 * (/in-house/projects/:id or /supplier-portal/projects/:id). The same React
 * component renders in both cases — only the API base differs. */

import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, NavigationArrow, ListChecks, CheckCircle, Clock, Folder } from "@phosphor-icons/react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Skeleton } from "../components/ui/skeleton";
import { fmtDate } from "../lib/dateUtil";

/** Build a maps URL that respects the user's device — Apple Maps on iOS,
 * Google Maps elsewhere. Falls back to a plain search query. */
function getDirectionsUrl(address) {
  if (!address) return null;
  const q = encodeURIComponent(address);
  const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "") || "";
  if (/iPhone|iPad|iPod|Macintosh/i.test(ua)) {
    return `https://maps.apple.com/?daddr=${q}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}

const STATUS_STYLES = {
  not_started: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  assigned: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  completed_by_vendor: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  approved: "bg-emerald-200 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-200",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

export default function MyProjectDetailPage({ mode = "in_house" }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const apiBase = mode === "vendor" ? "/supplier-portal" : "/in-house-portal";
  const taskRoutePrefix = mode === "vendor" ? "/supplier-portal/tasks" : "/in-house/projects";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`${apiBase}/projects/${projectId}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load project");
    } finally { setLoading(false); }
  }, [apiBase, projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const project = data?.project;
  const tasks = data?.tasks || [];
  const fullAddress = useMemo(() => {
    if (!project) return "";
    const parts = [
      project.property_address, project.property_postcode || project.postcode,
    ].filter(Boolean);
    return parts.join(", ");
  }, [project]);
  const directionsUrl = getDirectionsUrl(fullAddress || project?.property_address);

  const completedCount = tasks.filter(t => ["approved", "completed_by_vendor"].includes(t.status)).length;
  const completionPct = tasks.length ? Math.round((completedCount / tasks.length) * 100) : 0;

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4 space-y-4 pwa-page">
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (!project) {
    return <div className="max-w-3xl mx-auto p-6 text-center text-sm text-muted-foreground">Project not found.</div>;
  }

  const taskTo = (t) => mode === "vendor"
    ? `/supplier-portal/tasks/${t.id}`
    : `/in-house/projects/${projectId}/tasks/${t.id}`;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 pwa-page" data-testid="my-project-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        data-testid="my-project-back-btn"
      >
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header card */}
      <div className="rounded-xl border border-border bg-card p-5 sm:p-6 mb-5 shadow-sm">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Folder className="h-5 w-5 text-primary" weight="duotone" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] truncate" data-testid="my-project-name">{project.name}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{project.reference}</p>
          </div>
          {project.status && (
            <Badge variant="secondary" className="capitalize shrink-0">{(project.status || "").replace(/_/g, " ")}</Badge>
          )}
        </div>

        {/* Address + Directions */}
        {(fullAddress || project.property_address) && (
          <div className="rounded-lg bg-muted/40 p-3 mb-3" data-testid="my-project-address-block">
            <div className="flex items-start gap-2 mb-2">
              <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div className="text-sm flex-1 min-w-0">
                <p className="font-medium leading-tight">{fullAddress || project.property_address}</p>
                {project.property_name && <p className="text-xs text-muted-foreground mt-0.5">{project.property_name}</p>}
              </div>
            </div>
            {directionsUrl && (
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium active:scale-[0.98] transition"
                data-testid="my-project-directions-btn"
              >
                <NavigationArrow size={16} weight="fill" /> Get Directions
              </a>
            )}
          </div>
        )}

        {/* Site contact */}
        {project.site_contact_name && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="my-project-site-contact">
            <Phone size={14} />
            <span>{project.site_contact_name}</span>
            {project.site_contact_phone && (
              <a href={`tel:${project.site_contact_phone}`} className="text-primary hover:underline">
                {project.site_contact_phone}
              </a>
            )}
          </div>
        )}

        {/* Progress */}
        {tasks.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
              <span>{completedCount} of {tasks.length} tasks complete</span>
              <span className="font-semibold">{completionPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${completionPct}%` }}
                data-testid="my-project-progress-bar"
              />
            </div>
          </div>
        )}
      </div>

      {/* Tasks */}
      <div className="flex items-center gap-2 mb-3">
        <ListChecks size={18} className="text-amber-500" weight="duotone" />
        <h2 className="text-base font-semibold font-[Chivo]">My tasks on this project</h2>
        <Badge variant="secondary">{tasks.length}</Badge>
      </div>
      {tasks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="my-project-no-tasks">
          You have no tasks assigned on this project yet.
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map(t => {
            const overdue = t.due_date && !["approved", "completed_by_vendor"].includes(t.status) && new Date(t.due_date) < new Date();
            return (
              <Link
                key={t.id}
                to={taskTo(t)}
                className="block rounded-lg border border-border bg-card p-4 hover:border-primary/60 transition-colors active:scale-[0.99]"
                data-testid={`my-project-task-${t.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-semibold text-sm leading-tight truncate">{t.title}</h3>
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${STATUS_STYLES[t.status] || STATUS_STYLES.not_started}`}>
                        {(t.status || "new").replace(/_/g, " ")}
                      </span>
                      {overdue && <Badge variant="destructive" className="uppercase text-[9px]">Overdue</Badge>}
                    </div>
                    {t.description && <p className="text-xs text-muted-foreground line-clamp-2">{t.description}</p>}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-muted-foreground">
                      {t.start_date && <span className="inline-flex items-center gap-1"><Clock size={11}/> Start {fmtDate(t.start_date)}{t.start_time ? ` · ${t.start_time}` : ""}</span>}
                      {t.due_date && <span className="inline-flex items-center gap-1"><Clock size={11}/> Due {fmtDate(t.due_date)}</span>}
                      {(t.files || []).filter(f => f.kind === "photo").length > 0 && (
                        <span className="inline-flex items-center gap-1"><CheckCircle size={11}/> {(t.files || []).filter(f => f.kind === "photo").length} photo(s)</span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
